import { useCallback, useEffect, useState } from 'react';
import { fetchCycleData } from '../lib/userDataService';
import { PHASES } from '../components/cycle/phaseData';

/**
 * useCycleData(userId)
 *
 * Single source of truth for the Cycle experience.
 * Reads persisted cycle state from Supabase (`user_cycle_data`) via the
 * existing `userDataService.fetchCycleData`, derives the current phase +
 * day + waiting state, and returns a stable shape that the Cycle Screen,
 * Living Orb, cards and Today's Intelligence can all consume.
 *
 *   { loading, error, phase, dayNumber, cycleLength, waiting, data, refetch }
 *
 * `data` matches the shape used by `PHASES[phase]` in phaseData.js so
 * every existing UI element keeps rendering without a rewrite. Phase-
 * static editorial copy (sentence, focus, meta lines) is still sourced
 * from phaseData; user-specific numbers (dayNumber, next-period text)
 * override the demo values at render time.
 */

const MS_PER_DAY = 24 * 60 * 60 * 1000;

const derivePhase = (cycleDay, cycleLength) => {
  if (cycleDay > cycleLength) return 'waiting';
  if (cycleDay <= 5)  return 'menstrual';
  if (cycleDay <= 13) return 'follicular';
  if (cycleDay <= 16) return 'ovulatory';
  return 'luteal';
};

const daysSince = (isoDate) => {
  if (!isoDate) return null;
  const start = new Date(isoDate).getTime();
  if (Number.isNaN(start)) return null;
  return Math.max(0, Math.floor((Date.now() - start) / MS_PER_DAY));
};

const formatDate = (d) => d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });

const buildViewData = ({ phase, dayNumber, cycleLength, waiting, expectedNext }) => {
  const base = PHASES[waiting ? 'waiting' : phase] || PHASES.luteal;
  const daysUntil = expectedNext
    ? Math.round((expectedNext.getTime() - Date.now()) / MS_PER_DAY)
    : null;

  // Override the demo copy with the user's real numbers where relevant.
  const meta = [];
  if (waiting) {
    meta.push({ primary: 'Late — treated as still active', kind: 'late' });
    meta.push({ primary: 'You are in charge — log when it starts.', kind: 'mute' });
  } else if (daysUntil !== null && daysUntil > 0) {
    meta.push({ primary: `Next in ${daysUntil} day${daysUntil === 1 ? '' : 's'}`, kind: 'strong' });
    meta.push({ primary: `Expected ${formatDate(expectedNext)}`, kind: 'mute' });
  } else if (phase === 'menstrual') {
    meta.push({ primary: `Day ${dayNumber} of period`,  kind: 'strong' });
    meta.push({ primary: 'Rest, hydrate, be gentle.',   kind: 'mute' });
  } else {
    meta.push(...base.card.meta);
  }

  return {
    ...base,
    day: dayNumber,
    of: waiting ? '' : `/ ${cycleLength}`,
    waiting,
    card: {
      ...base.card,
      day: String(dayNumber),
      meta,
    },
  };
};

export default function useCycleData(userId) {
  const [state, setState] = useState({
    loading: true, error: null,
    phase: 'luteal', dayNumber: 21, cycleLength: 28, waiting: false,
    data: PHASES.luteal, expectedNext: null,
  });

  const load = useCallback(async () => {
    if (!userId) {
      setState((s) => ({ ...s, loading: false }));
      return;
    }
    setState((s) => ({ ...s, loading: true, error: null }));
    try {
      const cycle = await fetchCycleData(userId);
      if (!cycle || !cycle.lastPeriodDate) {
        // No cycle data yet — keep sensible default without crashing UI.
        setState({
          loading: false, error: null,
          phase: 'luteal', dayNumber: 1, cycleLength: 28, waiting: false,
          data: PHASES.luteal, expectedNext: null,
        });
        return;
      }
      const cycleLength = cycle.cycleLength || 28;
      const dSince = daysSince(cycle.lastPeriodDate) ?? 0;
      const dayNumber = dSince + 1; // Day 1 == first day of last period
      const waiting = dayNumber > cycleLength;
      const phase = derivePhase(dayNumber, cycleLength);
      const expectedNext = new Date(new Date(cycle.lastPeriodDate).getTime() + cycleLength * MS_PER_DAY);
      const data = buildViewData({ phase, dayNumber, cycleLength, waiting, expectedNext });
      setState({ loading: false, error: null, phase, dayNumber, cycleLength, waiting, data, expectedNext });
    } catch (err) {
      setState((s) => ({ ...s, loading: false, error: err?.message || 'Failed to load cycle' }));
    }
  }, [userId]);

  useEffect(() => { load(); }, [load]);

  return { ...state, refetch: load };
}
